import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MentorCardInfoSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const MIN_SKILLS = 3;
const MAX_SKILLS = 10;

function MentorCardInfoSubTab() {
  const [profiles, setProfiles] = useState([]);
  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [mainText, setMainText] = useState('');
  const [skills, setSkills] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/mentor-profiles`).then((res) => {
      if (!cancelled) setProfiles(res.data || []);
    }).catch(() => {
      if (!cancelled) setError('Не удалось загрузить список менторов');
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedMentorId) {
      setCard(null);
      setMainText('');
      setSkills([]);
      setEditMode(false);
      return;
    }
    setLoading(true);
    setError(null);
    axios.get(`${API_URL}/api/mentor-card/${selectedMentorId}`)
      .then((res) => {
        const data = res.data;
        setCard(data);
        setMainText(data.main_text || '');
        const sk = Array.isArray(data.skills) ? [...data.skills] : [];
        setSkills(sk.length >= MIN_SKILLS ? sk : [...sk, ...Array(MIN_SKILLS - sk.length).fill('')]);
        setEditMode(false);
      })
      .catch((err) => {
        setError(err.response?.data?.detail || err.message || 'Ошибка загрузки карточки');
        setCard(null);
      })
      .finally(() => setLoading(false));
  }, [selectedMentorId]);

  const mentorLabel = (id) => {
    const p = profiles.find((x) => x.id === Number(id));
    if (!p) return `ID ${id}`;
    const name = [p.first_name, p.last_name].filter(Boolean).join(' ');
    return name ? `${name} (${p.login})` : p.login;
  };

  const addSkill = () => {
    if (skills.length >= MAX_SKILLS) return;
    setSkills((prev) => [...prev, '']);
  };

  const removeSkill = (index) => {
    if (skills.length <= MIN_SKILLS) return;
    setSkills((prev) => prev.filter((_, i) => i !== index));
  };

  const setSkillAt = (index, value) => {
    setSkills((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSave = async () => {
    const nonEmpty = skills.map((s) => String(s).trim()).filter(Boolean);
    if (nonEmpty.length < MIN_SKILLS) {
      setSaveError(`Укажите минимум ${MIN_SKILLS} навыка.`);
      return;
    }
    if (nonEmpty.length > MAX_SKILLS) {
      setSaveError(`Не более ${MAX_SKILLS} навыков.`);
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/mentor-card/${selectedMentorId}`, {
        main_text: mainText,
        skills: nonEmpty
      });
      setCard(res.data);
      setMainText(res.data.main_text || '');
      const sk = Array.isArray(res.data.skills) ? res.data.skills : [];
      setSkills(sk.length >= MIN_SKILLS ? sk : [...sk, ...Array(MIN_SKILLS - sk.length).fill('')]);
      setEditMode(false);
    } catch (err) {
      setSaveError(err.response?.data?.detail || err.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!card) return false;
    const currentText = mainText;
    const savedText = card.main_text || '';
    if (currentText !== savedText) return true;
    const currentSk = skills.map((s) => String(s).trim()).filter(Boolean);
    const savedSk = Array.isArray(card.skills) ? card.skills : [];
    if (currentSk.length !== savedSk.length) return true;
    return currentSk.some((s, i) => s !== savedSk[i]);
  };

  const cancelEdit = () => {
    if (hasChanges() && !window.confirm('Есть несохранённые изменения. Закрыть?')) return;
    setMainText(card?.main_text || '');
    const sk = Array.isArray(card?.skills) ? [...card.skills] : [];
    setSkills(sk.length >= MIN_SKILLS ? sk : [...sk, ...Array(MIN_SKILLS - sk.length).fill('')]);
    setSaveError(null);
    setEditMode(false);
  };

  return (
    <div className="mentor-card-info-sub-tab">
      <div className="tab-header">
        <h2>Карточки менторов</h2>
      </div>
      <p className="tab-description">
        Выберите ментора, чтобы просмотреть и при необходимости отредактировать текст карточки и список навыков.
      </p>
      {error && <div className="error-message">{error}</div>}

      <div className="card-info-filter">
        <label htmlFor="card_info_mentor">Ментор</label>
        <select
          id="card_info_mentor"
          value={selectedMentorId}
          onChange={(e) => setSelectedMentorId(e.target.value)}
          className="filter-select"
        >
          <option value="">— Выберите ментора —</option>
          {profiles.map((p) => (
            <option key={p.id} value={p.id}>
              {mentorLabel(p.id)}
            </option>
          ))}
        </select>
      </div>

      {!selectedMentorId && (
        <div className="card-info-placeholder">
          Выберите ментора из списка выше.
        </div>
      )}

      {selectedMentorId && loading && (
        <div className="card-info-loading">Загрузка карточки...</div>
      )}

      {selectedMentorId && !loading && card && (
        <div className="card-info-block">
          <div className="card-info-block-header">
            <h3>Карточка: {mentorLabel(selectedMentorId)}</h3>
            {!editMode ? (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditMode(true)} title="Редактировать">
                ✏️ Редактировать
              </button>
            ) : (
              <button type="button" className="btn btn-secondary btn-sm" onClick={cancelEdit} title="Закрыть редактирование">
                ✕ Закрыть
              </button>
            )}
          </div>

          {editMode ? (
            <div className="card-info-form">
              <div className="form-group">
                <label>Основной текст карточки</label>
                <textarea
                  value={mainText}
                  onChange={(e) => setMainText(e.target.value)}
                  rows={6}
                  placeholder="Краткое описание опыта и подхода к менторству"
                  className="card-info-textarea"
                />
              </div>
              <div className="form-group">
                <label>Навыки (от {MIN_SKILLS} до {MAX_SKILLS})</label>
                <ul className="card-info-skills-list">
                  {skills.map((skill, index) => (
                    <li key={index} className="card-info-skill-row">
                      <input
                        type="text"
                        value={skill}
                        onChange={(e) => setSkillAt(index, e.target.value)}
                        placeholder={`Навык ${index + 1}`}
                        className="card-info-skill-input"
                      />
                      <button
                        type="button"
                        className="btn-icon btn-remove"
                        onClick={() => removeSkill(index)}
                        disabled={skills.length <= MIN_SKILLS}
                        title="Удалить навык"
                        aria-label="Удалить навык"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
                {skills.length < MAX_SKILLS && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addSkill}>
                    + Добавить навык
                  </button>
                )}
              </div>
              {saveError && <div className="error-message">{saveError}</div>}
              <div className="form-actions">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Сохранение…' : 'Сохранить изменения'}
                </button>
              </div>
            </div>
          ) : (
            <div className="card-info-view">
              <section className="card-info-view-section">
                <h4>Основной текст</h4>
                <div className="card-info-view-text">{card.main_text?.trim() || '—'}</div>
              </section>
              <section className="card-info-view-section">
                <h4>Навыки</h4>
                <ul className="card-info-view-skills">
                  {(Array.isArray(card.skills) ? card.skills : []).filter(Boolean).length === 0 ? (
                    <li className="card-info-view-skill empty">—</li>
                  ) : (
                    (Array.isArray(card.skills) ? card.skills : []).filter(Boolean).map((s, i) => (
                      <li key={i} className="card-info-view-skill">{s}</li>
                    ))
                  )}
                </ul>
              </section>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MentorCardInfoSubTab;
